import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";
import { cn } from "@/lib/utils";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useAuth, useClerk, useSession, useUser } from "@clerk/clerk-react";
import { PersonIcon } from "@radix-ui/react-icons";
import { useFeatureFlagEnabled } from "posthog-js/react";
import React from "react";
import { UserAvatar } from "../common/UserAvatar";
import Icon, { ICONS } from "../Core/Icons";
import { getPlanNameFromKey } from "../Pricing/plans";
import { useUserPreferencesStore } from "../UserPreferences/userPreferencesStore";
import { Settings as SettingsIcon } from "lucide-react";

interface UserMenuProps {
  extraItems?: React.ReactNode;
}

const UserMenu = React.memo(({ extraItems }: UserMenuProps) => {
  const { user } = useUser();
  const { signOut } = useAuth();

  const { buildUserProfileUrl } = useClerk();

  const rawSubscriptionTier = usePlanInfoStore(
    (state) => state.subscriptionTier,
  );
  const setShowUpgradeModal = usePlanInfoStore(
    (state) => state.setShowUpgradeModal,
  );
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);
  const { session } = useSession();
  const { remainingUsage } = useUsageLimits();
  const openPreferences = useUserPreferencesStore((state) => state.openModal);

  const showBuildVersion = useFeatureFlagEnabled("show-build-version");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="select-none focus:outline-hidden cursor-pointer h-full flex items-center justify-center">
        {user && (
          <UserAvatar
            userName={
              user?.fullName ||
              user?.primaryEmailAddress?.emailAddress ||
              "Unknown"
            }
            userId={user.externalId || ""}
            className="text-xs h-7 w-7"
          />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        style={{ zIndex: 10000 }}
        className="w-56"
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="cursor-default flex flex-col items-start text-xs opacity-100!"
            disabled={true}
          >
            <div className="flex flex-col">{user?.fullName}</div>
            <div>{user?.primaryEmailAddress?.emailAddress}</div>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="flex text-xs flex-col items-start justify-center cursor-pointer"
          onClick={() => {
            window.open(buildUserProfileUrl(), "_blank");
          }}
        >
          <div className="flex items-center gap-1">
            <PersonIcon className="w-4 h-4 mx-1 text-text-muted" />
            <div className="">User Settings</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={openPreferences}
          className="cursor-pointer text-xs"
        >
          <div className="flex items-center gap-1">
            <SettingsIcon className="w-4 h-4 mx-1 text-text-muted" />
            <div className="">Preferences</div>
          </div>
        </DropdownMenuItem>
        {extraItems}
        <DropdownMenuItem
          onClick={() => {
            // Delay the modal from opening until the dropdown menu is closed
            // So that the pointer events on the body are set correctly
            setTimeout(() => {
              setShowUpgradeModal(true);
              refreshPlanInfo(session);
            }, 300);
          }}
          className={cn(
            "cursor-pointer text-xs",
            !rawSubscriptionTier && "hidden",
          )}
        >
          <span className="flex items-start justify-between w-full">
            <div className="flex items-center gap-1">
              <Icon icon={ICONS.CARD} className="w-4 h-4 mx-1" />
              <div className="">My Plan</div>
            </div>
            <div className="text-text-secondary">
              {getPlanNameFromKey(rawSubscriptionTier)}
            </div>
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            signOut({
              redirectUrl: window.location.origin,
            });
          }}
          className="cursor-pointer text-xs"
        >
          <div className="flex items-center gap-1">
            <Icon
              icon={ICONS.ARROW_RIGHT_START_ON_RECTANGLE}
              className="w-4 h-4 mx-1"
            />
            <div className="">Log Out</div>
          </div>
        </DropdownMenuItem>
        {showBuildVersion && <DropdownMenuSeparator />}
        {showBuildVersion && (
          <DropdownMenuItem
            className="cursor-default flex flex-col items-start text-xs opacity-100!"
            disabled={true}
          >
            <div className="text-gray-500 text-xs pt-0.5 select-all">
              Build: {__BUILD_VERSION__} ({__BUILD_COMMIT__.slice(0, 8)})
            </div>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default UserMenu;
